import { useState } from 'react';

import { useAuth } from '../providers';
import {
  AuthErrorResponse,
  AuthGateway,
  GenerateMultipassTokenResponse,
} from '../types/Auth';
import { GiftCardCheckoutPayload } from '../types/GiftCard';
import {
  CheckoutGateway,
  GiftCardPurchasingController,
  ShopifyProductPurchaseDetails,
} from '../types/Purchasing';
import { useCurrency } from './useCurrency';
import { useOnBfCacheLoad } from './useOnBfCacheLoad';

export type Props = {
  reset: (payload: unknown) => void;
  checkoutGateway: CheckoutGateway<
    ShopifyProductPurchaseDetails,
    GiftCardCheckoutPayload
  >;
  authGateway: AuthGateway;
};

export const useGiftCardPurchasing = ({
  authGateway,
  checkoutGateway,
  reset,
}: Props): GiftCardPurchasingController => {
  const [currency] = useCurrency();
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const { isLoggedIn } = useAuth();

  useOnBfCacheLoad(() => {
    reset({
      giftCardVariantId: null,
      recipientName: null,
      senderName: null,
      message: null,
    });
    setCheckoutLoading(false);
  });

  const goToCheckout = (payload: GiftCardCheckoutPayload) => {
    if (payload.giftCardVariantId === null) return;
    setCheckoutLoading(true);
    checkoutGateway
      .checkoutWithGiftCard(payload, currency)
      .then(async (result) => {
        if (!isLoggedIn) {
          window.location.href = result;
          return;
        }

        const multipassTokenResponse =
          await authGateway.generateShopifyMultipassToken(result);

        if (!(multipassTokenResponse as AuthErrorResponse).error) {
          window.location.href = `${process.env.NEXT_PUBLIC_MULTIPASS_URL}${
            (multipassTokenResponse as GenerateMultipassTokenResponse).token
          }`;
        }
      });
  };

  return {
    checkoutLoading,
    goToCheckout,
  };
};
